<template>
  <div class="projects-page">
    <PlanGate
      feature="projects_module"
      title="Módulo de Proyectos"
      description="Agrupa facturas por proyecto, sigue su rentabilidad y controla gastos. Disponible en el plan Crece o superior."
    >
      <!-- ── Project Detail View ── -->
      <ProjectDetail
        v-if="selectedProject"
        :project-id="selectedProject.id"
        @back="selectedProject = null"
        @payment-registered="onPaymentRegistered"
      />

      <!-- ── Main List View ── -->
      <template v-else>
        <div class="projects-page__topbar">
          <div class="projects-page__title">
            <h2>Proyectos</h2>
            <span class="projects-page__sub">Gestiona tus proyectos</span>
          </div>
          <el-button
            type="primary"
            class="btn-primary-custom"
            @click="showProjectForm = true"
          >
            + Nuevo proyecto
          </el-button>
        </div>

        <!-- Summary cards -->
        <div class="summary-cards">
          <template v-if="loading">
            <div
              class="summary-card summary-card--skeleton"
              v-for="i in 4"
              :key="i"
            >
              <span class="sk-line sk-line--label"></span>
              <span class="sk-line sk-line--value"></span>
            </div>
          </template>
          <template v-else>
            <div class="summary-card">
              <span class="summary-card__label">Proyectos activos</span>
              <span class="summary-card__value">{{ activeCount }}</span>
            </div>
            <div class="summary-card">
              <span class="summary-card__label">Ingresos en proyectos</span>
              <span class="summary-card__value"
                >${{ formatNum(totalInvoiced) }}</span
              >
            </div>
            <div class="summary-card">
              <span class="summary-card__label">Gastos en proyectos</span>
              <span class="summary-card__value"
                >${{ formatNum(totalExpenses) }}</span
              >
            </div>
            <div class="summary-card summary-card--green">
              <span class="summary-card__label">Utilidad proyectos</span>
              <span class="summary-card__value"
                >${{ formatNum(totalProfit) }}</span
              >
            </div>
          </template>
        </div>

        <!-- Project cards grid -->
        <div v-if="loading" class="projects-grid">
          <div class="project-card-skeleton" v-for="i in 4" :key="i">
            <div class="pcs__header">
              <span class="sk-line sk-line--title"></span>
              <span class="sk-badge"></span>
            </div>
            <div class="pcs__metrics">
              <div class="pcs__metric" v-for="j in 4" :key="j">
                <span class="sk-line sk-line--label"></span>
                <span class="sk-line sk-line--metric"></span>
              </div>
            </div>
            <span class="sk-line sk-line--progress"></span>
            <div class="pcs__footer">
              <span class="sk-line sk-line--date"></span>
              <span class="sk-line sk-line--date"></span>
            </div>
          </div>
        </div>
        <div v-else-if="projects.length === 0" class="projects-empty">
          <p>No tienes proyectos aún.</p>
          <el-button
            type="primary"
            class="btn-primary-custom"
            @click="showProjectForm = true"
          >
            Crear primer proyecto
          </el-button>
        </div>
        <div v-else class="projects-grid">
          <ProjectCard
            v-for="p in projects"
            :key="p.id"
            :project="p"
            @click="openProject"
          />
        </div>
      </template>

      <!-- Modals -->
      <ProjectFormModal
        v-if="showProjectForm"
        @close="showProjectForm = false"
        @created="onProjectCreated"
      />

      <WrapperProjectsTourComponent />
    </PlanGate>
  </div>
</template>

<script>
import projectService from "./services/projectService";
import ProjectCard from "./components/ProjectCard";
import ProjectDetail from "./components/ProjectDetail";
import ProjectFormModal from "./components/ProjectFormModal";
import WrapperProjectsTourComponent from "./components/WrapperTour";
import PlanGate from "@/components/PlanGate";

export default {
  name: "ProjectsPage",
  components: {
    ProjectCard,
    ProjectDetail,
    ProjectFormModal,
    PlanGate,
    WrapperProjectsTourComponent
  },
  data() {
    return {
      projects: [],
      loading: false,
      selectedProject: null,
      showProjectForm: false
    };
  },
  computed: {
    hasAccess() {
      return this.$store.getters.hasFeature("projects_module");
    },
    userPlanLoaded() {
      return this.$store.getters.userPlanLoaded;
    },
    activeCount() {
      return this.projects.filter(p => p.status === "active").length;
    },
    totalInvoiced() {
      return this.projects.reduce((s, p) => s + (p.total_invoiced || 0), 0);
    },
    totalExpenses() {
      return this.projects.reduce((s, p) => s + (p.total_expenses || 0), 0);
    },
    totalProfit() {
      return this.projects.reduce((s, p) => s + (p.gross_profit || 0), 0);
    }
  },
  watch: {
    userPlanLoaded: {
      immediate: true,
      handler(loaded) {
        if (loaded && this.hasAccess) this.loadData();
      }
    }
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const res = await projectService.getProjects();
        if (res.data.success) this.projects = res.data.data;
      } catch (e) {
        this.$notifications.error({ message: "Error cargando proyectos." });
      } finally {
        this.loading = false;
      }
    },
    openProject(project) {
      this.selectedProject = project;
    },
    onProjectCreated(project) {
      this.showProjectForm = false;
      this.projects.unshift(project);
    },
    onPaymentRegistered() {
      this.loadData();
    },
    formatNum(val) {
      if (!val && val !== 0) return "0.00";
      return Number(val).toLocaleString("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.projects-page {
  padding: 24px;

  &__topbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }

  &__title h2 {
    font-size: 22px;
    font-weight: 700;
    color: #2d2b55;
    margin: 0 0 2px;
  }

  &__sub {
    font-size: 13px;
    color: #aaa;
  }
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.summary-card {
  background: #fff;
  border: 1.5px solid #eef0f8;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  &--green {
    border-color: #b7f4e8;
  }

  &__label {
    font-size: 11px;
    color: #aaa;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &__value {
    font-size: 22px;
    font-weight: 700;
    color: #2d2b55;
  }
}

.projects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}

.projects-loading,
.projects-empty {
  text-align: center;
  padding: 60px 20px;
  color: #aaa;
  font-size: 14px;
}

/* ── Skeleton shimmer ── */
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.sk-line {
  display: block;
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f0f5 25%, #e4e4ec 50%, #f0f0f5 75%);
  background-size: 200% 100%;
  animation: shimmer 1.3s ease-in-out infinite;

  &--label {
    width: 80px;
    height: 11px;
  }
  &--value {
    width: 120px;
    height: 26px;
    margin-top: 4px;
  }
  &--title {
    width: 55%;
    height: 16px;
  }
  &--metric {
    width: 75px;
    height: 18px;
    margin-top: 4px;
  }
  &--progress {
    width: 100%;
    height: 6px;
    border-radius: 10px;
  }
  &--date {
    width: 90px;
    height: 12px;
  }
}

.sk-badge {
  width: 52px;
  height: 22px;
  border-radius: 20px;
  background: linear-gradient(90deg, #f0f0f5 25%, #e4e4ec 50%, #f0f0f5 75%);
  background-size: 200% 100%;
  animation: shimmer 1.3s ease-in-out infinite;
}

.summary-card--skeleton {
  gap: 8px;
}

/* ── Project card skeleton ── */
.project-card-skeleton {
  background: #fff;
  border: 1.5px solid #eef0f8;
  border-radius: 14px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.pcs {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__metrics {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
  }

  &__metric {
    display: flex;
    flex-direction: column;
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    margin-top: 4px;
  }
}

.btn-primary-custom {
  background-color: #3fdbbf;
  border: none;
  border-radius: 10px;
  color: #fff;
  font-weight: 600;

  &:hover,
  &:focus {
    background-color: #0e8c87;
    color: #fff;
  }
}

@media (max-width: 768px) {
  .projects-page {
    padding: 16px;

    &__topbar {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }
  }

  .summary-cards {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .summary-card__value {
    font-size: 18px;
  }
}
</style>
